// components/AI/ChatImageAttach.js
// Tampilan lampiran gambar (mode Coding) — dipisah dari AIChat.js.
// Murni presentasional: seluruh state & handler datang dari useChatImages.
import React from 'react';
import { ImagePlus, Loader2, Plus, FileText } from 'lucide-react';
import { humanBytes } from '../../utils/imageAttach';

// Strip thumbnail di atas kolom input.
export function ChatImageStrip({ img, t }) {
  if (!img.attachedImgs.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {img.attachedImgs.map((a, i) => (
        <div key={a.id} className="relative group border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
          <img src={a.uri} alt={a.name} className="w-14 h-14 object-cover block" />
          {/* Nomor urut: gambar dikirim ke model sesuai urutan ini, jadi user
              bisa merujuk "gambar 2" di pertanyaannya. */}
          <span className="absolute top-0 left-0 bg-brand-600 text-white text-[9px] leading-none px-1 py-0.5 rounded-br">{i + 1}</span>
          <button onClick={() => img.removeImage(a.id)}
            title={t('aiChat.removeImage')}
            className="absolute top-0 right-0 w-4 h-4 bg-slate-900/70 text-white text-[10px] leading-none flex items-center justify-center rounded-bl">×</button>
          <span className="absolute bottom-0 inset-x-0 bg-slate-900/60 text-white text-[9px] text-center leading-tight">{humanBytes(a.bytes)}</span>
        </div>
      ))}
    </div>
  );
}

// Strip chip untuk BERKAS TEKS. Dipisah dari ChatImageStrip walau tampak
// serupa: gambar punya pratinjau visual (thumbnail 14×14) yang jadi identitas
// utamanya, sedangkan berkas teks tidak punya apa pun untuk dilihat — yang
// membedakannya cuma nama. Menyatukan keduanya berarti satu komponen dengan
// dua tata letak yang tidak berbagi apa-apa selain wadah.
export function ChatTextFileStrip({ files, t }) {
  if (!files.files.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {files.files.map(f => (
        <div key={f.id}
          className="flex items-center gap-1.5 border border-slate-200 rounded-lg bg-slate-50 px-2 py-1 max-w-[220px]">
          <FileText style={{ width: 12, height: 12 }} className="text-brand-500 flex-shrink-0" />
          <span className="text-[11px] font-mono text-slate-700 truncate" title={f.name}>{f.name}</span>
          <span className="text-[10px] text-slate-400 flex-shrink-0">{humanBytes(f.bytes)}</span>
          <button onClick={() => files.removeFile(f.id)}
            title={LA.removeFile}
            className="text-slate-400 hover:text-rose-600 text-xs leading-none flex-shrink-0">×</button>
        </div>
      ))}
    </div>
  );
}

// String lokal, TIDAK di translations.js. Berkas itu punya gerbang CI prebuild
// (check-i18n.js) yang menuntut paritas penuh id/en/ar, dan fallback
// `t('k') || 'default'` tidak menyelamatkan build. Yang sudah ada di
// translations.js (aiChat.attachImage dsb.) tetap dipakai lewat `t`.
const LA = {
  attach:     'Lampirkan',
  menuImage:  'Gambar',
  menuText:   'Berkas teks',
  menuTextHint: '.log .csv .json .md .txt — maks 50 KB',
  removeFile: 'Lepas berkas',
};

// Tombol `+` — SATU pintu masuk untuk semua lampiran.
//
// Menggantikan tombol gambar yang dulu berdiri sendiri. Sebelumnya lampiran
// tersebar di dua tempat yang sama sekali tidak berhubungan secara visual:
// tombol gambar di kiri kolom input, dan unggah zip/dokumen di bar atas yang
// harus dibuka dulu. Dari sudut pandang pengguna keduanya "melampirkan
// berkas", dan tidak ada dasar untuk menebak yang mana.
//
// Menunya SENGAJA dua pilihan bernama, BUKAN satu pemilih berkas yang
// merutekan diam-diam berdasarkan ekstensi. Perutean senyap akan menyamarkan
// perbedaan yang justru penting: gambar ikut ke giliran ini lalu hilang,
// sementara berkas teks isinya menyatu ke pertanyaan. Kalau keduanya terasa
// identik, orang akan menaruh berkas 2 MB dan bingung kenapa ditolak.
export function ChatAttachButton({ img, files, t, disabled }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  // Klik di luar menutup menu. WAJIB pakai listener dokumen: menu ini
  // mengambang di atas kolom input, dan tanpa ini ia tetap terbuka saat user
  // mulai mengetik — menutupi teks yang sedang ditulis.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const busy = img.imgBusy || files.busy;
  const pick = (fn) => { setOpen(false); fn(); };

  return (
    <div className="relative flex-shrink-0" ref={wrapRef}>
      <input ref={img.imgInputRef} type="file" className="hidden" multiple
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={img.onFileSelected} />
      <input ref={files.inputRef} type="file" className="hidden" multiple
        accept=".log,.csv,.json,.md,.txt"
        onChange={files.onFileSelected} />

      <button onClick={() => setOpen(o => !o)}
        disabled={disabled || busy}
        title={LA.attach}
        className="w-9 h-9 border border-slate-200 hover:border-brand-400 hover:text-brand-600 disabled:opacity-40 text-slate-400 rounded-xl flex items-center justify-center transition-all">
        {busy
          ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
          : <Plus style={{ width: 16, height: 16 }} />}
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1.5 w-56 bg-white border border-slate-200
                        rounded-xl shadow-lg overflow-hidden z-20">
          <button onClick={() => pick(img.openPicker)} disabled={img.atMax}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-slate-700
                       hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white">
            <ImagePlus style={{ width: 13, height: 13 }} className="text-slate-400 flex-shrink-0" />
            <span>{LA.menuImage}</span>
          </button>
          <button onClick={() => pick(files.openPicker)} disabled={files.atMax}
            className="w-full flex items-start gap-2 px-3 py-2 text-left text-xs text-slate-700
                       border-t border-slate-100 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white">
            <FileText style={{ width: 13, height: 13 }} className="text-slate-400 flex-shrink-0 mt-0.5" />
            <span>
              {LA.menuText}
              <span className="block text-[10px] text-slate-400 font-mono">{LA.menuTextHint}</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
